import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaCaretDown } from "react-icons/fa";
import { SlArrowRight } from "react-icons/sl";
import { useUser } from "../../../../context/user-context";
import { formatAmount } from "../../../../utils/formatTime";
import GetRates from "../../../../components/utils/GetRates";
import useAuthAxios from "../../../../utils/baseAxios";
import { API } from "../../../../constants/api";
import {
  AirtimePaymentSource,
  AirtimeProvider,
} from "./types";
import PaymentSourceSelect from "../PaymentSourceSelect";
import { NAIRA_WALLET_ENABLED } from "../../../../constants/constants";

type BuyAirtimeProps = {
  setOpenAirtime: (open: boolean) => void;
  setOpenAirtimePreview: (open: boolean) => void;
  amount: string;
  setAmount: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  network: string;
  setNetwork: (value: string) => void;
  networkName: string;
  setNetworkName: (value: string) => void;
  paymentSource: AirtimePaymentSource;
  setPaymentSource: (value: AirtimePaymentSource) => void;
};

const BuyAirtime: React.FC<BuyAirtimeProps> = ({
  setOpenAirtime,
  setOpenAirtimePreview,
  amount,
  setAmount,
  phone,
  setPhone,
  network,
  setNetwork,
  networkName,
  setNetworkName,
  paymentSource,
  setPaymentSource,
}) => {
  const axiosInstance = useAuthAxios();
  const { userDetails } = useUser();
  const walletBalances = userDetails?.user.deexWalletBalances;
  const [openNet, setOpenNet] = useState(false);
  const [displayAmount, setDisplayAmount] = useState("");

  const usdtBalance = walletBalances?.usdt ?? 0;
  const ngnBalance = walletBalances?.ngn ?? 0;
  const usdtRate = GetRates("USDT");

  const { data: providersResponse, isLoading: loadingProviders, isError } = useQuery({
    queryKey: ["airtime-providers"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getAirtimeProviders);
      return response.data;
    },
    retry: 1,
  });

  const providers: AirtimeProvider[] = useMemo(
    () => providersResponse?.data ?? [],
    [providersResponse?.data]
  );

  // Whole naira only — the purchase endpoint's `amount` is an integer.
  const isNumber = (str: string) => /^\d*$/.test(str);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, "");
    if (isNumber(value)) {
      setAmount(value);
      setDisplayAmount(value);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (amount) {
        setDisplayAmount(Number(amount).toLocaleString("en-US"));
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [amount]);

  const isBalanceSufficient =
    paymentSource === "NGN"
      ? Number(amount) <= Number(ngnBalance)
      : Number(amount) / Number(usdtRate) <= Number(usdtBalance);

  const validate = () => {
    if (!amount) {
      toast.error("Kindly enter a valid amount");
      return false;
    }
    // API minimum for airtime is ₦50.
    if (Number(amount) < 50) {
      toast.error("Minimum airtime purchase is 50 NGN");
      return false;
    }
    if (!network) {
      toast.error("Kindly select a network");
      return false;
    }
    if (!phone || phone.length < 11) {
      toast.error("Kindly enter a valid phone number");
      return false;
    }
    if (!isBalanceSufficient) {
      toast.error("Insufficient funds");
      return false;
    }
    return true;
  };

  const handleProceed = () => {
    if (!validate()) return;
    setOpenAirtime(false);
    setOpenAirtimePreview(true);
  };

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load airtime providers");
    }
  }, [isError]);

  useEffect(() => {
    if (providersResponse === undefined) return;
    if (!providers.length) {
      toast.error("No airtime providers available");
    }
  }, [providers.length, providersResponse]);

  return (
    <div className="fixed inset-0 flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:w-2/5 xxl:w-1/3">
        <div className="flex w-full items-center justify-start gap-6">
          <button type="button" onClick={() => setOpenAirtime(false)}>
            <BsArrowLeft className="text-[20px] text-black dark:text-white" />
          </button>
          <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
            Buy Airtime
          </h4>
        </div>

        <div className="mt-10 w-full">
          <PaymentSourceSelect
            value={paymentSource}
            onChange={setPaymentSource}
          />

          <div className="mt-8 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">Amount</h4>
            <div className="mt-3 flex w-full items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 shadow dark:border-gray-700">
              <h4 className="text-[13px] font-bold text-black dark:text-white">
                NGN
              </h4>
              <div className="w-[40%]">
                <div className="flex w-full items-center justify-end text-gray-900 dark:text-gray-200">
                  <span className="font-sora text-[18px] font-bold">₦</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={displayAmount}
                    onChange={handleChange}
                    style={{ width: `${Math.max(displayAmount.length, 4)}ch` }}
                    className="bg-transparent text-right text-[18px] font-semibold outline-none"
                  />
                </div>
                {paymentSource === "USDT" && (
                  <h4 className="mt-1 pr-2 text-right text-[12px] font-medium text-gray-500">
                    {displayAmount
                      ? `${formatAmount(Number(amount) / Number(usdtRate))} USDT`
                      : "0.00 USDT"}
                  </h4>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 xs:grid-cols-4">
            {["500", "1000", "2000", "5000"].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setAmount(preset)}
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-gray-100 py-3 dark:border-gray-700"
              >
                <span className="text-[16px] text-gray-600 dark:text-gray-400">
                  ₦{Number(preset).toLocaleString("en-US")}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-2 flex items-center justify-between">
            {NAIRA_WALLET_ENABLED && (
              <h4 className="text-[12px] font-bold text-[#BE6B0A]">
                Balance:{" "}
                {paymentSource === "NGN"
                  ? `₦${formatAmount(ngnBalance)}`
                  : `${formatAmount(usdtBalance)} USDT`}
              </h4>
            )}
            {!isBalanceSufficient && amount && (
              <h4 className="text-[12px] font-bold italic text-red-600">
                Insufficient funds
              </h4>
            )}
          </div>

          <div className="relative mt-6 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">Network</h4>
            <button
              type="button"
              onClick={() => setOpenNet((prev) => !prev)}
              className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 px-4 py-3 text-[14px] capitalize shadow dark:border-gray-700"
            >
              {network ? (
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                  {networkName}
                </span>
              ) : (
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                  {loadingProviders ? "Loading networks..." : "Select network"}
                </span>
              )}
              <FaCaretDown className="pointer-events-none text-[20px] text-gray-900 dark:text-gray-200" />
            </button>

            {openNet && (
              <div className="absolute z-50 mt-2 max-h-[280px] w-full overflow-auto rounded-xl bg-white p-2 shadow-lg dark:bg-gray-800">
                {loadingProviders ? (
                  <p className="p-4 text-center text-[13px] text-gray-500">
                    Loading...
                  </p>
                ) : (
                  providers.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      onClick={() => {
                        setNetwork(provider.slug);
                        setNetworkName(provider.name);
                        setOpenNet(false);
                      }}
                      className="mt-2 flex w-full cursor-pointer items-center justify-between rounded-xl border border-gray-200 p-4 dark:border-gray-600"
                    >
                      <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                        {provider.name}
                      </span>
                      <SlArrowRight className="text-[15px] text-gray-600 dark:text-gray-400" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="mt-8">
            <h4 className="text-[14px] font-bold text-gray-500">
              Phone Number
            </h4>
            <input
              type="tel"
              placeholder="Enter phone number"
              value={phone}
              maxLength={11}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value.length > 11) value = value.slice(0, 11);
                if (value === "" || value[0] === "0") {
                  setPhone(value);
                }
              }}
              className="mt-3 h-[45px] w-full rounded-[4px] border border-[#EAEAEA] bg-transparent p-3 text-left text-[13px] text-gray-700 shadow outline-none dark:border-gray-700 dark:text-gray-300"
            />
          </div>

          <button
            type="button"
            disabled={
              !amount ||
              !network ||
              !phone ||
              !isBalanceSufficient ||
              loadingProviders
            }
            onClick={handleProceed}
            className={
              !amount ||
              !network ||
              !phone ||
              !isBalanceSufficient ||
              loadingProviders
                ? "mt-12 flex h-[48px] w-full items-center justify-center rounded-xl bg-gray-400 text-[15px] font-medium text-gray-200"
                : "mt-12 flex h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-medium text-white"
            }
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyAirtime;
